import { useAlert } from "@context/AlertContext";

import {
	CheckCircleIcon,
	WarningCircleIcon,
	XCircleIcon,
} from "@phosphor-icons/react";
import { IconInfoCircleFilled, IconX } from "@tabler/icons-react";
import React, { FC, useEffect, useState } from "react";

interface AlertToastProps {
	classname?: string;
}

const AlertToast: FC<AlertToastProps> = ({ classname = "" }) => {
	const { alertData, clearAlert } = useAlert();
	const [progress, setProgress] = useState(100);
	const message = alertData?.message;
	const link = alertData?.link;
	const linkText = alertData?.linkText;
	const type = alertData?.type;
	const SuccessIcon = (
		<CheckCircleIcon size={19.5} className="text-[#34C759]" />
	);
	const ErrorIcon = <XCircleIcon size={19.5} className="text-[#d71d10]" />;
	const InfoIcon = <IconInfoCircleFilled className="text-[#007AFF]" />;
	const WarningIcon = (
		<WarningCircleIcon size={19.5} className="text-[#FF9500]" />
	);

	const SuccessColor = "#34C759";
	const ErrorColor = "#d71d10";
	const InfoColor = "#007AFF";
	const WarningColor = "#FF9500";
	const ProgressColor =
		type === "success"
			? SuccessColor
			: type === "error"
				? ErrorColor
				: type === "info"
					? InfoColor
					: WarningColor;

	const IconComponent =
		type === "success"
			? SuccessIcon
			: type === "error"
				? ErrorIcon
				: type === "info"
					? InfoIcon
					: WarningIcon;

	useEffect(() => {
		if (alertData) {
			setProgress(100);
			const duration = 3000; // 3 seconds
			const interval = 15; // Update every 15ms
			const steps = duration / interval;
			const decrement = 100 / steps;

			const timer = setInterval(() => {
				setProgress((prevProgress) => {
					if (prevProgress <= 0) {
						clearInterval(timer);
						return 0;
					}
					return prevProgress - decrement;
				});
			}, interval);

			return () => clearInterval(timer);
		}
	}, [alertData]);

	if (!alertData) return null;

	return (
		<div className="fixed top-24 z-[999] right-6 w-[17.25rem] h-[5.625rem] bg-white drop-shadow-md rounded-lg flex flex-col justify-between ">
			<div className="flex flex-row gap-2 px-4 pt-4 pb-2 relative h-full">
				{IconComponent}
				<div className="flex flex-col gap-3 flex-1">
					<p className="text-sm font-normal text-[#666666]">{message}</p>
					{alertData?.link && (
						<a
							href={link}
							className="text-sm font-normal text-black underline leading-tight cursor-pointer"
							style={{ textUnderlineOffset: 4 }}
						>
							{linkText}
						</a>
					)}
				</div>
				<button
					className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100 transition"
					onClick={clearAlert}
					aria-label="Close alert"
					style={{ lineHeight: 0 }}
				>
					<IconX size={12} className="text-[#9E9E9E]" />
				</button>
			</div>
			<div className="w-full h-1 bg-[#E9FCEB] rounded-b-lg overflow-hidden">
				<div
					className="h-full rounded-b-lg"
					style={{ backgroundColor: ProgressColor, width: `${progress}%` }}
				/>
			</div>
		</div>
	);
};

export default AlertToast;
